import type { PopoverProps } from "@mui/material/Popover";
import Popover from "@mui/material/Popover";
import type { FC, HTMLProps, MouseEvent, ReactNode } from "react";
import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";

type HoverPopoverProps = HoverPopover["popoverProps"] & { children: ReactNode };

export const HoverPopover: FC<HoverPopoverProps> = ({ children, ...popoverProps }) => {
  return (
    <Popover
      {...popoverProps}
      sx={{
        "pointerEvents": "none",
        "& .MuiPopover-paper": {
          borderRadius: 1,
          boxShadow: theme => theme.shadows[1],
          border: theme => `1px solid ${theme.palette.grey["300"]}`,
        },
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      disableRestoreFocus
      transitionDuration={200}
    >
      {children}
    </Popover>
  );
};

export type HoverPopover = ReturnType<typeof useHoverPopover>;

export const useHoverPopover = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [state, setState] = useState<"hover-anchor" | "hover-popover" | null>(null);

  const handleMouseOverAnchor = useDebouncedCallback((event: MouseEvent<HTMLElement>) => {
    setState("hover-anchor");
    setAnchorEl(event.target as HTMLElement);
  }, 250);

  const handleAnchorMouseLeave = () => {
    handleMouseOverAnchor.cancel();
    setState(state => (state === "hover-popover" ? state : null));
  };

  const handlePopoverMouseOver = () => setState("hover-popover");

  const handlePopoverMouseLeave = () => setState(null);

  const popoverId = "hoverPopover";

  const open = state === "hover-anchor" || state === "hover-popover";

  const anchorProps = {
    "onMouseOver": handleMouseOverAnchor,
    "onMouseLeave": handleAnchorMouseLeave,
    "aria-owns": open ? popoverId : undefined,
    "aria-haspopup": true,
  } satisfies HTMLProps<unknown>;

  const popoverProps: PopoverProps = {
    anchorEl,
    open,
    id: popoverId,
    slotProps: {
      paper: {
        sx: { pointerEvents: "auto" },
        onMouseOver: handlePopoverMouseOver,
        onMouseLeave: handlePopoverMouseLeave,
      },
    },
  };

  return { anchorProps, popoverProps };
};
